import { ApiResponse } from "../../shared/types"

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  // Only set Content-Type default when there's a body AND caller hasn't already set it
  const hasBody = init?.body !== undefined && init?.body !== null;
  const callerHeaders = new Headers(init?.headers as HeadersInit | undefined);
  const headers = new Headers(callerHeaders);
  if (hasBody && !callerHeaders.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(path, { ...init, headers });

  // Guard against non-JSON responses (e.g. 502/503 HTML from a CDN)
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    throw new Error(`Server returned ${res.status} ${res.statusText}`);
  }

  const json = (await res.json()) as ApiResponse<T>;

  // HIGH-4 fix: Split checks so valid `null` data doesn't throw, and HTTP
  // errors vs API-level errors are reported with distinct messages.
  if (!res.ok) {
    throw new Error((json as any).error || `HTTP ${res.status} ${res.statusText}`);
  }
  if (!json.success) {
    throw new Error(json.error || 'API returned success: false');
  }
  return json.data as T;
}

/**
 * Walks a paginated `{ items, next }` endpoint to completion and returns the
 * combined list. Backend list endpoints cap page size to stay within
 * Cloudflare's per-request subrequest budget (each record is its own
 * Durable Object), so large datasets (e.g. elephants) need multiple small
 * requests instead of one big one.
 */
export async function fetchAllPages<T>(path: string, init?: RequestInit): Promise<T[]> {
  const items: T[] = [];
  let cursor: string | null = null;
  let guard = 0;
  do {
    const url = cursor ? `${path}${path.includes('?') ? '&' : '?'}cursor=${encodeURIComponent(cursor)}` : path;
    const page = await api<{ items: T[]; next: string | null }>(url, init);
    items.push(...page.items);
    cursor = page.next;
    guard++;
  } while (cursor && guard < 100);
  return items;
}